import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Save,
  Settings as SettingsIcon,
} from 'lucide-react';
import { apiClient, API_ENDPOINTS, useAuthStore } from '@dukene/shared';

interface StoreData {
  id: string;
  name: string;
  subdomain: string;
  email: string;
  phone: string | null;
  status: string;
  // Flat business address fields
  streetAddress: string | null;
  city: string | null;
  county: string | null;
  postalCode: string | null;
  country: string | null;
  settings: {
    currency?: string;
    timezone?: string;
    taxRate?: number;
    shippingFee?: number;
    freeShippingThreshold?: number;
    orderPrefix?: string;
    enableInventoryTracking?: boolean;
    allowBackorders?: boolean;
    requiresShippingAddress?: boolean;
    paymentMethods?: string[];
    whatsappOrderNotificationsEnabled?: boolean;
    whatsappOrderNotificationsPhone?: string;
  } | null;
  createdAt: string;
  updatedAt: string;
}

interface EditableStore {
  name?: string;
  email?: string;
  phone?: string | null;
  streetAddress?: string;
  city?: string;
  county?: string;
  postalCode?: string;
  country?: string;
  settings?: Partial<StoreData['settings']>;
}

const CURRENCIES = [
  { code: 'KES', name: 'Kenyan Shilling (KES)', symbol: 'KSh' },
  { code: 'USD', name: 'US Dollar (USD)', symbol: '$' },
  { code: 'EUR', name: 'Euro (EUR)', symbol: '€' },
];

const TIMEZONES = [
  { value: 'Africa/Nairobi', label: 'Africa/Nairobi (EAT)' },
  { value: 'America/New_York', label: 'America/New_York (EST)' },
  { value: 'Europe/London', label: 'Europe/London (GMT)' },
];

export default function Settings() {
  const [isEditing, setIsEditing] = useState(false);
  const [editedStore, setEditedStore] = useState<EditableStore>({});
  const activeStoreId = useAuthStore((state) => state.activeStoreId);

  const queryClient = useQueryClient();

  // Fetch store data
  const { data: store, isLoading, error } = useQuery<StoreData>({
    queryKey: ['merchant-store', activeStoreId],
    queryFn: () => apiClient.get<StoreData>(API_ENDPOINTS.MERCHANT.STORE),
    enabled: !!activeStoreId,
  });

  // Update store mutation
  const updateStoreMutation = useMutation({
    mutationFn: (data: EditableStore) =>
      apiClient.patch(API_ENDPOINTS.MERCHANT.STORE, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchant-store', activeStoreId] });
      setIsEditing(false);
      setEditedStore({});
    },
  });

  const handleEdit = () => {
    if (!store) return;
    setIsEditing(true);
    setEditedStore({
      name: store.name,
      email: store.email,
      phone: store.phone,
      streetAddress: store.streetAddress || '',
      city: store.city || '',
      county: store.county || '',
      postalCode: store.postalCode || '',
      country: store.country || 'KE',
      settings: store.settings || {},
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedStore({});
  };

  const handleSave = () => {
    const notificationSettings = editedStore.settings;
    if (notificationSettings?.whatsappOrderNotificationsEnabled && !/^\+[1-9]\d{7,14}$/.test(notificationSettings.whatsappOrderNotificationsPhone || '')) {
      alert('Enter a WhatsApp number in international format, for example +254712345678.');
      return;
    }
    updateStoreMutation.mutate(editedStore);
  };

  const updateSettings = (key: string, value: any) => {
    setEditedStore({
      ...editedStore,
      settings: {
        ...(editedStore.settings || {}),
        [key]: value,
      },
    });
  };

  if (isLoading) return <div className="flex h-64 items-center justify-center">Loading...</div>;
  if (error || !store) return <div className="p-8 text-red-600">Failed to load store settings</div>;

  const currentStore = isEditing
    ? { ...store, ...editedStore, settings: { ...store.settings, ...editedStore.settings } }
    : store;
  const settings = currentStore.settings || {};

  // Current address values (flat fields)
  const streetAddress = isEditing ? editedStore.streetAddress ?? store.streetAddress ?? '' : store.streetAddress ?? '';
  const city = isEditing ? editedStore.city ?? store.city ?? '' : store.city ?? '';
  const county = isEditing ? editedStore.county ?? store.county ?? '' : store.county ?? '';
  const postalCode = isEditing ? editedStore.postalCode ?? store.postalCode ?? '' : store.postalCode ?? '';
  const country = isEditing ? editedStore.country ?? store.country ?? 'KE' : store.country ?? 'KE';

  const addressComplete = !!(store.streetAddress && store.city);

  return (
    <div className="space-y-6 pb-20 max-w-4xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-[#D2D5D9] dark:border-slate-800">
        <div>
          <h1 className="text-xl font-bold text-[#202223] dark:text-white">Store details</h1>
        </div>
        {!isEditing && (
          <button
            onClick={handleEdit}
            className="inline-flex items-center gap-2 px-3.5 py-2 bg-[#202223] dark:bg-white text-white dark:text-slate-900 font-medium text-xs rounded-lg shadow-sm hover:opacity-90 transition-all self-start sm:self-auto"
          >
            <SettingsIcon className="w-3.5 h-3.5" />
            Edit Store Details
          </button>
        )}
      </div>

      <div className="space-y-6">
        {/* Core Identity Card */}
        <div className="bg-white dark:bg-slate-900 border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-6 shadow-sm space-y-5">
          <div>
            <h3 className="text-sm font-bold text-[#202223] dark:text-white">Basic information</h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-[#202223] dark:text-slate-300 mb-1.5">
                Store name
              </label>
              <input
                value={currentStore.name}
                disabled={!isEditing}
                onChange={(e) => setEditedStore({ ...editedStore, name: e.target.value })}
                className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-sm font-medium text-[#202223] dark:text-white disabled:bg-[#F1F2F4] dark:disabled:bg-slate-800/50 disabled:text-[#5C5F62] dark:disabled:text-slate-400 focus:border-emerald-600 focus:outline-none"
                placeholder="e.g. Acme Corp"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-[#202223] dark:text-slate-300 mb-1.5">
                  Store contact email
                </label>
                <input
                  value={currentStore.email}
                  disabled={!isEditing}
                  onChange={(e) => setEditedStore({ ...editedStore, email: e.target.value })}
                  className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-sm font-medium text-[#202223] dark:text-white disabled:bg-[#F1F2F4] dark:disabled:bg-slate-800/50 disabled:text-[#5C5F62] dark:disabled:text-slate-400 focus:border-emerald-600 focus:outline-none"
                  placeholder="help@yourstore.com"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#202223] dark:text-slate-300 mb-1.5">
                  Store phone number
                </label>
                <input
                  value={currentStore.phone || ''}
                  disabled={!isEditing}
                  onChange={(e) => setEditedStore({ ...editedStore, phone: e.target.value })}
                  className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-sm font-medium text-[#202223] dark:text-white disabled:bg-[#F1F2F4] dark:disabled:bg-slate-800/50 disabled:text-[#5C5F62] dark:disabled:text-slate-400 focus:border-emerald-600 focus:outline-none"
                  placeholder="+254..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Merchant WhatsApp notifications */}
        <div className="bg-white dark:bg-slate-900 border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-6 shadow-sm space-y-5">
          <div>
            <h3 className="text-sm font-bold text-[#202223] dark:text-white">Paid order notifications</h3>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-1">Receive a WhatsApp message when a customer pays for an order.</p>
          </div>
          <label className="flex items-center gap-3 text-sm text-[#202223] dark:text-white">
            <input type="checkbox" checked={settings.whatsappOrderNotificationsEnabled || false} disabled={!isEditing}
              onChange={(e) => updateSettings('whatsappOrderNotificationsEnabled', e.target.checked)} />
            Send paid order notifications to WhatsApp
          </label>
          <div>
            <label className="block text-xs font-semibold text-[#202223] dark:text-slate-300 mb-1.5">WhatsApp notification number</label>
            <input type="tel" value={settings.whatsappOrderNotificationsPhone || ''} disabled={!isEditing}
              onChange={(e) => updateSettings('whatsappOrderNotificationsPhone', e.target.value)}
              className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-[#202223] dark:text-white disabled:bg-[#F1F2F4] dark:disabled:text-slate-400 focus:border-emerald-600 focus:outline-none"
              placeholder="+254712345678" />
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-1">Use the number that should receive alerts, including its country code.</p>
          </div>
        </div>

        {/* Physical Headquarters Card */}
        <div className="bg-white dark:bg-slate-900 border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-6 shadow-sm space-y-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <h3 className="text-sm font-bold text-[#202223] dark:text-white">Billing & Headquarters address</h3>
            </div>
            {addressComplete ? (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300">
                GMC Eligible
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300">
                Address Missing
              </span>
            )}
          </div>

          {!addressComplete && (
            <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/40 rounded-lg">
              <span className="text-amber-500 text-base leading-none">⚠️</span>
              <div>
                <p className="text-xs font-bold text-amber-800 dark:text-amber-300">Add address for GMC feed verification.</p>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-[#202223] dark:text-slate-300 mb-1.5">
                Street address
              </label>
              <input
                value={streetAddress}
                disabled={!isEditing}
                onChange={(e) => setEditedStore({ ...editedStore, streetAddress: e.target.value })}
                className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-sm font-medium text-[#202223] dark:text-white disabled:bg-[#F1F2F4] dark:disabled:bg-slate-800/50 disabled:text-[#5C5F62] dark:disabled:text-slate-400 focus:border-emerald-600 focus:outline-none"
                placeholder="e.g. 123 Moi Avenue"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-[#202223] dark:text-slate-300 mb-1.5">
                  City
                </label>
                <input
                  value={city}
                  disabled={!isEditing}
                  onChange={(e) => setEditedStore({ ...editedStore, city: e.target.value })}
                  className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-sm font-medium text-[#202223] dark:text-white disabled:bg-[#F1F2F4] dark:disabled:bg-slate-800/50 disabled:text-[#5C5F62] dark:disabled:text-slate-400 focus:border-emerald-600 focus:outline-none"
                  placeholder="Nairobi"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#202223] dark:text-slate-300 mb-1.5">
                  County / State
                </label>
                <input
                  value={county}
                  disabled={!isEditing}
                  onChange={(e) => setEditedStore({ ...editedStore, county: e.target.value })}
                  className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-sm font-medium text-[#202223] dark:text-white disabled:bg-[#F1F2F4] dark:disabled:bg-slate-800/50 disabled:text-[#5C5F62] dark:disabled:text-slate-400 focus:border-emerald-600 focus:outline-none"
                  placeholder="Nairobi County"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-[#202223] dark:text-slate-300 mb-1.5">
                  Postal code
                </label>
                <input
                  value={postalCode}
                  disabled={!isEditing}
                  onChange={(e) => setEditedStore({ ...editedStore, postalCode: e.target.value })}
                  className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-sm font-medium text-[#202223] dark:text-white disabled:bg-[#F1F2F4] dark:disabled:bg-slate-800/50 disabled:text-[#5C5F62] dark:disabled:text-slate-400 focus:border-emerald-600 focus:outline-none"
                  placeholder="00100"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#202223] dark:text-slate-300 mb-1.5">
                  Country code (ISO)
                </label>
                <input
                  value={country}
                  disabled={!isEditing}
                  onChange={(e) => setEditedStore({ ...editedStore, country: e.target.value.toUpperCase().slice(0, 2) })}
                  className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-sm font-medium text-[#202223] dark:text-white disabled:bg-[#F1F2F4] dark:disabled:bg-slate-800/50 disabled:text-[#5C5F62] dark:disabled:text-slate-400 focus:border-emerald-600 focus:outline-none"
                  placeholder="KE"
                  maxLength={2}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Standards and formats Card */}
        <div className="bg-white dark:bg-slate-900 border border-[#D2D5D9] dark:border-slate-800 rounded-xl p-6 shadow-sm space-y-5">
          <div>
            <h3 className="text-sm font-bold text-[#202223] dark:text-white">Store currency and timezone</h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-[#202223] dark:text-slate-300 mb-1.5">
                Store currency
              </label>
              <select
                value={settings.currency || 'KES'}
                disabled={!isEditing}
                onChange={(e) => updateSettings('currency', e.target.value)}
                className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-sm font-medium text-[#202223] dark:text-white disabled:bg-[#F1F2F4] dark:disabled:bg-slate-800/50 disabled:text-[#5C5F62] dark:disabled:text-slate-400 focus:border-emerald-600 focus:outline-none"
              >
                {CURRENCIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#202223] dark:text-slate-300 mb-1.5">
                Timezone
              </label>
              <select
                value={settings.timezone || 'Africa/Nairobi'}
                disabled={!isEditing}
                onChange={(e) => updateSettings('timezone', e.target.value)}
                className="w-full h-9 px-3 rounded-lg border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-800 text-sm font-medium text-[#202223] dark:text-white disabled:bg-[#F1F2F4] dark:disabled:bg-slate-800/50 disabled:text-[#5C5F62] dark:disabled:text-slate-400 focus:border-emerald-600 focus:outline-none"
              >
                {TIMEZONES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Save Bar */}
      {isEditing && (
        <div className="sticky bottom-4 z-40 flex items-center justify-between px-5 py-3.5 bg-[#202223] text-white rounded-xl shadow-xl border border-slate-700 animate-in slide-in-from-bottom-3 duration-200">
          <span className="text-xs font-medium text-slate-200">Unsaved store details</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="px-3.5 py-1.5 text-xs font-medium text-slate-300 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={updateStoreMutation.isPending}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg shadow-sm transition-colors disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" />
              {updateStoreMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
